import { makeObservable, observable, action, runInAction, reaction } from 'mobx';
import {
  fetchTokenCost,
  fetchContextOverload,
  fetchCompaction,
  fetchRouterAccuracy,
  fetchSafetyFlakiness,
  fetchToolOverflowRisk,
  fetchA2ALatency,
  fetchRunLatency,
  fetchErrorMix,
  fetchToolPerformance,
  fetchThreadContext,
  fetchConversationCost,
  type TokenCostBucket,
  type ContextOverloadBucket,
  type CompactionBucket,
  type RouterAccuracyBucket,
  type SafetyFlakinessBucket,
  type ToolOverflowBucket,
  type A2ALatencyBucket,
  type RunLatencyBucket,
  type ErrorMixBucket,
  type ToolPerformanceBucket,
  type ThreadContextBucket,
  type ConversationCostReport,
} from '@/api/insights';
import type { RootStore } from './RootStore';

// Holds the seven insight result sets + a shared "days" window. Refetches
// when the merchant changes or the user picks a different window. All
// queries fire in parallel so the page lights up as data lands.

export class InsightsStore {
  @observable days = 30;
  @observable minSafetyRuns = 2;

  /**
   * Optional agent filter applied to the Tool overflow tile. Set via the
   * `?agentId=N` query string on /insights — drives the dashboard
   * pressure-row drill-in. null = merchant-wide (legacy default).
   */
  @observable agentFilter: number | null = null;

  @observable tokenCost: TokenCostBucket[] = [];
  @observable contextOverload: ContextOverloadBucket[] = [];
  @observable compaction: CompactionBucket[] = [];
  @observable routerAccuracy: RouterAccuracyBucket[] = [];
  @observable safetyFlakiness: SafetyFlakinessBucket[] = [];
  @observable toolOverflow: ToolOverflowBucket[] = [];
  @observable a2aLatency: A2ALatencyBucket[] = [];

  // Tier 2 — added 2026-05-01.
  @observable runLatency: RunLatencyBucket[] = [];
  @observable errorMix: ErrorMixBucket[] = [];
  @observable toolPerformance: ToolPerformanceBucket[] = [];
  @observable threadContext: ThreadContextBucket[] = [];
  @observable conversationCost: ConversationCostReport | null = null;

  @observable loading: Record<string, boolean> = {};
  @observable errors: Record<string, string | null> = {};

  constructor(private root: RootStore) {
    makeObservable(this);

    // Refetch when merchant changes.
    reaction(
      () => this.root.merchantContextStore.selectedMerchantId,
      (merchantId) => {
        if (merchantId != null) {
          void this.fetchAll();
        } else {
          this.clear();
        }
      }
    );
  }

  @action setDays(v: number): void {
    this.days = v;
    void this.fetchAll();
  }

  @action setMinSafetyRuns(v: number): void {
    this.minSafetyRuns = v;
    void this.fetchSafety();
  }

  @action
  async fetchAll(): Promise<void> {
    await Promise.all([
      this.fetchTokenCost(),
      this.fetchContextOverload(),
      this.fetchCompaction(),
      this.fetchRouterAccuracy(),
      this.fetchSafety(),
      this.fetchToolOverflow(),
      this.fetchA2ALatency(),
      // Tier 2 — fire in parallel with the rest.
      this.fetchRunLatency(),
      this.fetchErrorMix(),
      this.fetchToolPerformance(),
      this.fetchThreadContext(),
      this.fetchConversationCost(),
    ]);
  }

  private async run<T>(
    key: string,
    work: () => Promise<T>,
    setData: (v: T) => void
  ): Promise<void> {
    runInAction(() => {
      this.loading[key] = true;
      this.errors[key] = null;
    });
    try {
      const data = await work();
      runInAction(() => {
        setData(data);
        this.loading[key] = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading[key] = false;
        this.errors[key] = err instanceof Error ? err.message : 'Failed';
      });
    }
  }

  @action fetchTokenCost = (): Promise<void> =>
    this.run('tokenCost', () => fetchTokenCost(this.days), (d) => (this.tokenCost = d));

  @action fetchContextOverload = (): Promise<void> =>
    this.run('contextOverload', () => fetchContextOverload(this.days), (d) => (this.contextOverload = d));

  @action fetchCompaction = (): Promise<void> =>
    this.run('compaction', () => fetchCompaction(this.days), (d) => (this.compaction = d));

  @action fetchRouterAccuracy = (): Promise<void> =>
    this.run('routerAccuracy', () => fetchRouterAccuracy(this.days), (d) => (this.routerAccuracy = d));

  @action fetchSafety = (): Promise<void> =>
    this.run('safetyFlakiness', () => fetchSafetyFlakiness(this.minSafetyRuns), (d) => (this.safetyFlakiness = d));

  @action fetchToolOverflow = (): Promise<void> =>
    this.run(
      'toolOverflow',
      () => fetchToolOverflowRisk(this.days, this.agentFilter),
      (d) => (this.toolOverflow = d),
    );

  /**
   * Set the agent filter and refetch the affected tile. Called by
   * InsightsPage when it parses ?agentId from the URL.
   */
  @action setAgentFilter(agentId: number | null): void {
    if (this.agentFilter === agentId) return;
    this.agentFilter = agentId;
    void this.fetchToolOverflow();
  }

  @action fetchA2ALatency = (): Promise<void> =>
    this.run('a2aLatency', () => fetchA2ALatency(this.days), (d) => (this.a2aLatency = d));

  // Tier 2 fetchers
  @action fetchRunLatency = (): Promise<void> =>
    this.run('runLatency', () => fetchRunLatency(this.days), (d) => (this.runLatency = d));

  @action fetchErrorMix = (): Promise<void> =>
    this.run('errorMix', () => fetchErrorMix(this.days), (d) => (this.errorMix = d));

  @action fetchToolPerformance = (): Promise<void> =>
    this.run('toolPerformance', () => fetchToolPerformance(this.days), (d) => (this.toolPerformance = d));

  @action fetchThreadContext = (): Promise<void> =>
    this.run('threadContext', () => fetchThreadContext(this.days), (d) => (this.threadContext = d));

  @action fetchConversationCost = (): Promise<void> =>
    this.run('conversationCost', () => fetchConversationCost(this.days), (d) => (this.conversationCost = d));

  @action
  clear(): void {
    this.tokenCost = [];
    this.contextOverload = [];
    this.compaction = [];
    this.routerAccuracy = [];
    this.safetyFlakiness = [];
    this.toolOverflow = [];
    this.a2aLatency = [];
    this.runLatency = [];
    this.errorMix = [];
    this.toolPerformance = [];
    this.threadContext = [];
    this.conversationCost = null;
  }
}
